<script setup lang="ts">
/**
 * This is the main panel for a single Gesuch (edit panel).
 */
import NavigationToggleGroup from '@/components/navigation-toggle-group.vue'
import GesuchCardAdressen from '@/views/auth/gesuch/GesuchAdressenCard.vue'
import GesuchCardBedingungen from '@/views/auth/gesuch/GesuchBedingungenCard.vue'
import GesuchCardProjekt from '@/views/auth/gesuch/GesuchProjektCard.vue'
import GesuchCardLiegenschaften from '@/views/auth/gesuch/GesuchLiegenschaftenCard.vue'
import GesuchCardDokumente from '@/views/auth/gesuch/GesuchDokumenteCard.vue'
import GesuchCardAbschluss from '@/views/auth/gesuch/GesuchAbschlussCard.vue'
import GesuchCardNachrichten from '@/views/auth/gesuch/GesuchNachrichtenCard.vue'
import GesuchCardKontoinformation from '@/views/auth/gesuch/GesuchKontoinformationCard.vue'
import StatusLabel from '@/components/gesuch-status-label.vue'
import NextActionBanner from '@/components/next-action-banner.vue'
import { ShWappenSvg, TgWappenSvg } from '@/helpers/images'
import { errToStr } from '@/helpers/tools.js'
import { instance as getApi } from '@/helpers/api.js'
import { computed, onBeforeUnmount, onMounted, ref, type Ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from '@/composables/toast'
import { Gesuch, GesuchPhase } from '@/models/Gesuch'
import { useAuthUser, useGlobals } from '@/helpers/globals'
import { createModel } from '@kp/js-model'
import { ApollonConfFk } from '@/models/ApollonConfFk'
import { Data } from '@/models/apollon_data/Data'
import { useDisplay } from 'vuetify'
import { ZeusDokument } from '@/models/zeus/ZeusDokument'

export type StepType = {
    icon: string
    ident: string
    to: { name: string; params?: { step: string } }
    showWhenReadonly?: boolean
    enabled: boolean
    enabledFn?: () => boolean | undefined
    text: string
    preventSaveOnExit?: boolean
    phases?: GesuchPhase[]
}

const gesuch: Ref<Gesuch | null> = ref(null)
const zeusDokumente: Ref<ZeusDokument[]> = ref([])
const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const toast = useToast()
const user = useAuthUser()
const liegenschaftEditingMode = ref(false)
const saving = ref(false)
const steps: Ref<StepType[]> = ref([
    {
        icon: 'far fa-address-book',
        ident: 'adressen',
        to: { name: 'gesuch' },
        showWhenReadonly: true,
        enabled: true,
        text: t('adressen'),
    },
    {
        icon: 'far fa-building',
        ident: 'liegenschaften',
        to: { name: 'gesuch-step', params: { nr: gesuch.value?.nummer, step: 'liegenschaften' } },
        showWhenReadonly: true,
        enabled: true,
        enabledFn: () => gesuch.value?.config?.config?.objekte?.visible,
        text: t('liegenschaften'),
    },
    {
        icon: 'fas fa-tasks',
        ident: 'projekt',
        to: { name: 'gesuch-step', params: { nr: gesuch.value?.nummer, step: 'projekt' } },
        showWhenReadonly: true,
        enabled: true,
        text: t('project'),
    },
    {
        icon: 'far fa-file-pdf',
        ident: 'dokumente',
        to: { name: 'gesuch-step', params: { nr: gesuch.value?.nummer, step: 'dokumente' } },
        showWhenReadonly: true,
        enabled: true,
        text: t('documents'),
    },
    {
        icon: 'fas fa-code-branch',
        ident: 'bedingungen',
        to: { name: 'gesuch-step', params: { nr: gesuch.value?.nummer, step: 'bedingungen' } },
        showWhenReadonly: true,
        enabled: true,
        text: t('foerderbedingungen'),
        preventSaveOnExit: true,
    },
    {
        icon: 'fas fa-square-root-alt',
        ident: 'foerdersaetze',
        to: { name: 'gesuch-step', params: { nr: gesuch.value?.nummer, step: 'foerdersaetze' } },
        showWhenReadonly: true,
        enabled: true,
        text: t('foerdersaetze'),
        preventSaveOnExit: true,
    },
    {
        icon: 'far fa-credit-card',
        ident: 'kontoinformation',
        to: { name: 'gesuch-step', params: { nr: gesuch.value?.nummer, step: 'kontoinformation' } },
        showWhenReadonly: true,
        enabled: true,
        text: t('kontoinformation'),
        phases: [GesuchPhase.ABSCHLUSS],
    },
    {
        icon: 'far fa-paper-plane',
        ident: 'abschluss',
        to: { name: 'gesuch-step', params: { nr: gesuch.value?.nummer, step: 'abschluss' } },
        showWhenReadonly: true,
        enabled: true,
        text: t('abschluss'),
    },
    {
        icon: 'far fa-envelope',
        ident: 'nachrichten',
        to: { name: 'gesuch-step', params: { nr: gesuch.value?.nummer, step: 'nachrichten' } },
        showWhenReadonly: true,
        enabled: true,
        text: t('messages'),
    },
])
const step: Ref<StepType> = ref(steps.value[0])

const { xs: isXs } = useDisplay()

const readOnly = computed(() => {
    return gesuch.value?.isLocked(user) || gesuch.value?.isReadOnly()
})

watch(route, (route, oldRoute) => {
    let steptype = steps.value.find((s) => s.ident === (route.params.step as string))
    if (!steptype) {
        steptype = steps.value.find((s) => s.ident === 'adressen')
    }
    next(steptype || null)

    // skip if the route has not changed:
    if (oldRoute && oldRoute.params.nr === route.params.nr) {
        return
    }
    if (oldRoute && oldRoute.params.nr) {
        releaseLock(oldRoute.params.nr as string)
    }
    load(route.params.nr as string)
})

function findStepByIdent(ident: string) {
    for (const s of steps.value) {
        if (s.ident === ident) {
            return s
        }
    }
    return null
}

onMounted(async () => {
    const loaded = await load(route.params.nr as string)
    if (loaded) {
        registerLockRefreshTimer()
        // renumberSteps()
        const val = route.params.step as string
        const foundStep = findStepByIdent(val)
        step.value = foundStep || steps.value[0]
    } else {
        router.go(-1)
    }
})

onBeforeUnmount(() => {
    unregisterLockRefreshTimer()
    if (gesuch.value) {
        releaseLock(gesuch.value.nummer)
    }
})

let lockRefreshTimer: number | null = null
function registerLockRefreshTimer() {
    unregisterLockRefreshTimer()
    // fires every 10 minutes:
    console.info('Starting Gesuch Lock Timer')
    lockRefreshTimer = setInterval(
        () => {
            if (gesuch.value) {
                refreshLock(gesuch.value?.nummer)
            }
        },
        1000 * 60 * 10,
    )
}

function unregisterLockRefreshTimer() {
    if (lockRefreshTimer) {
        clearInterval(lockRefreshTimer)
        lockRefreshTimer = null
        console.info('Stopping Gesuch Lock Timer')
    }
}

async function refreshLock(gesuchNr: string | null) {
    if (readOnly.value) {
        console.info('Not executing Gesuch lock, as this Gesuch is readonly: ' + gesuchNr)
        return
    }
    try {
        console.info('Refreshing lock on Gesuch ' + gesuchNr)
        await getApi().get('/gesuch/acquireLock', {
            params: {
                gesuch_nummer: gesuchNr,
            },
        })
    } catch (e) {
        toast.add({ text: errToStr(e), color: 'error' })
    }
}

async function releaseLock(gesuchNr: string | null) {
    try {
        console.info('Releasing lock on Gesuch ' + gesuchNr)
        await getApi().get('/gesuch/releaseLock', {
            params: {
                gesuch_nummer: gesuchNr,
            },
        })
    } catch (e) {
        toast.add({ text: errToStr(e), color: 'error' })
    }
}


async function load(gesuchNr: string) {
    try {
        const res = await getApi().get('/gesuch/load', {
            params: {
                gesuch_nummer: gesuchNr,
                for_writing: true,
            },
        })
        gesuch.value = createModel(Gesuch, res.data.gesuch)
        const config = createModel(ApollonConfFk, res.data.config)
        gesuch.value.set('config', config)
        for (const zd of res.data.zeus_dokumente || []) {
            zeusDokumente.value.push(createModel(ZeusDokument, zd))
        }
        return true
    } catch (e) {
        toast.add({ text: errToStr(e), color: 'error' })
        return false
    }
}

async function onGesuchDataChanged(data: Data | null) {
    await saveGesuch(data)
}

const locked = computed(() => {
    return gesuch.value?.isLocked(user)
})

async function saveGesuch(data: Data | null = null) {
    try {
        saving.value = true
        if (!readOnly.value) {
            if (data) {
                gesuch.value?.set('data', data)
            }
            await gesuch.value?.save()
        }
    } catch (res) {
        toast.add({ text: errToStr(res), color: 'error' })
        // Note: It may be that the Gesuch record is not out-of-sync with the backend:
        // Maybe we need to reload the actual state? or reset / rollback the record?
    } finally {
        saving.value = false
    }
}

async function next(nextStep: StepType | null) {
    let actStepFound = false
    let save = true
    let newStep = steps.value[0]

    if (step.value && step.value.preventSaveOnExit) {
        save = false
    }

    // find the NEXT step of the actual step, or use the given one:
    if (!nextStep) {
        steps.value.forEach((s) => {
            if (actStepFound) {
                if (isStepVisible(s)) {
                    newStep = s
                    actStepFound = false
                }
            }
            if (s.ident === step.value.ident) {
                actStepFound = true
            }
        })
    } else {
        newStep = nextStep
    }
    if (step.value.ident !== newStep.ident) {
        if (save) {
            await saveGesuch()
        }
        step.value = newStep
        if (newStep.to.params?.step !== router.currentRoute.value.params.step) {
            router.push(newStep.to).catch(() => console.log('oops, not routed'))
        }
    }
}

function isStepVisible(step: StepType) {
    let vis = step.showWhenReadonly || !readOnly.value
    if (typeof step.enabledFn === 'function') {
        vis = vis && (step.enabledFn() || false)
    }
    if (step.phases && step.phases.length > 0) {
        vis = vis && gesuch.value?.phase ? step.phases.indexOf(gesuch.value?.phase) >= 0 : false
    }
    return vis
}

function abschluss() {
    router.push({ name: 'gesuche' })
}

const gueltigkeitZusageDeadline = useGlobals().config?.gueltigkeit_zusage_deadline || 0
</script>

<template>
    <v-skeleton-loader
        v-if="!gesuch"
        class="centered-card"
        type="list-item-avatar, list-item-three-line@3, actions"
    ></v-skeleton-loader>
    <div v-else>
        <v-toolbar flat color="primary white--text" class="colored-toolbar" height="auto">
            <v-toolbar
                flat
                color="primary white--text"
                class="kp-large-container colored-toolbar"
                height="auto"
            >
                <v-btn icon color="white" :to="{ name: 'gesuche' }">
                    <v-icon>fas fa-arrow-left</v-icon>
                </v-btn>
                <div class="ml-2">
                    <div>
                        <v-toolbar-title>
                            <v-tooltip right>
                                <template v-slot:activator="{ props }">
                                    <v-icon
                                        v-if="
                                            gesuch.gueltigkeit_zusage_in_tagen !== null &&
                                            gesuch.gueltigkeit_zusage_in_tagen < 0 &&
                                            !gesuch.isClosed()
                                        "
                                        color="red"
                                        class="pt-1"
                                        v-bind="props"
                                    >
                                        fas fa-exclamation-circle
                                    </v-icon>
                                    <v-icon
                                        v-else-if="
                                            gesuch.gueltigkeit_zusage_in_tagen !== null &&
                                            gesuch.gueltigkeit_zusage_in_tagen <=
                                                gueltigkeitZusageDeadline &&
                                            !gesuch.isClosed()
                                        "
                                        color="orange"
                                        class="pt-1"
                                        v-bind="props"
                                    >
                                        fas fa-exclamation-circle
                                    </v-icon>
                                    <v-icon
                                        v-else-if="
                                            gesuch.gueltigkeit_zusage_in_tagen !== null &&
                                            gesuch.gueltigkeit_zusage_in_tagen >
                                                gueltigkeitZusageDeadline &&
                                            !gesuch.isClosed()
                                        "
                                        color="green"
                                        class="pt-1"
                                        v-bind="props"
                                    >
                                        fas fa-circle
                                    </v-icon>
                                </template>
                                <span
                                    v-if="
                                        gesuch.gueltigkeit_zusage_in_tagen &&
                                        gesuch.gueltigkeit_zusage_in_tagen > 0
                                    "
                                    >{{
                                        $t('zusage_deadline_in_days', {
                                            days: gesuch.gueltigkeit_zusage_in_tagen,
                                            date: gesuch.gueltigkeit_zusage?.format('DD.MM.YYYY'),
                                        })
                                    }}</span
                                >
                                <span v-else-if="gesuch.gueltigkeit_zusage_in_tagen === 0">{{
                                    $t('zusage_deadline_today', {
                                        date: gesuch.gueltigkeit_zusage?.format('DD.MM.YYYY'),
                                    })
                                }}</span>
                                <span v-else>{{
                                    $t('zusage_deadline_expired_since', {
                                        days: Math.abs(gesuch.gueltigkeit_zusage_in_tagen || 0),
                                        date: gesuch.gueltigkeit_zusage?.format('DD.MM.YYYY'),
                                    })
                                }}</span>
                            </v-tooltip>
                            {{ gesuch.nummer }}
                            <span v-if="locked && !readOnly" class="locked">
                                <v-icon color="red" x-small> fas fa-lock</v-icon>
                                {{ $t('locked') }}
                            </span>
                            <span v-if="readOnly" class="read-only">
                                <v-icon color="yellow" x-small>far fa-eye</v-icon>
                                {{ $t('read_only') }}
                            </span>
                        </v-toolbar-title>
                    </div>
                    <div
                        class="subtitle-2"
                        :style="isXs ? 'font-size:small; margin-bottom:0.5rem;' : ''"
                    >
                        {{ gesuch.foerderkategorie_apollon }}
                        |
                        {{ $t('created') }}: {{ gesuch.created?.format('DD.MM.YYYY') }}
                        <span v-if="gesuch.gesuch_erstmals_eingereicht_datum?.isValid()">
                            | {{ $t('submitted') }}:
                            {{ gesuch.gesuch_erstmals_eingereicht_datum.format('DD.MM.YYYY') }}
                        </span>
                        <span v-if="gesuch.foerderzusage_datum?.isValid()">
                            | {{ $t('foerderzusage') }}:
                            {{ gesuch.foerderzusage_datum.format('DD.MM.YYYY') }}
                        </span>
                        <span v-if="gesuch.projektabschluss_erstmals_eingereicht_datum?.isValid()">
                            | {{ $t('project_done') }}:
                            {{
                                gesuch.projektabschluss_erstmals_eingereicht_datum.format(
                                    'DD.MM.YYYY',
                                )
                            }}
                        </span>
                        <StatusLabel :status="gesuch.status" badge />
                    </div>
                </div>
                <v-spacer />
                <div v-if="gesuch.rolle" class="mr-5" :style="isXs ? 'font-size:small' : ''">
                    Ihre Rolle: {{ $t(gesuch.rolle) }}
                </div>
                <img
                    v-if="gesuch.kanton === 'SH'"
                    alt="Wappen SH"
                    width="40px"
                    :src="ShWappenSvg"
                    class="hidden-xs ma-2"
                />
                <img
                    v-if="gesuch.kanton === 'TG'"
                    alt="Wappen TG"
                    width="40px"
                    :src="TgWappenSvg"
                    class="hidden-xs ma-2"
                />
            </v-toolbar>
        </v-toolbar>

        <div class="kp-large-container px-4">
            <NavigationToggleGroup
                v-model="step.ident"
                :items="steps.filter(isStepVisible)"
                :loading="saving"
            />

            <NextActionBanner :gesuch="gesuch" />
            <v-alert
                v-if="gesuch.isLocked(user)"
                class="ma-1"
                outlined
                color="error"
                icon="far fa-eye"
            >
                <strong>{{ $t('gesuch_readonly') }}</strong>
            </v-alert>
        </div>

        <div
            v-if="step"
            :class="{
                'd-block': true,
                'd-md-none': true,
            }"
        >
            <h6 class="pr-3 pl-3 grey-lighten-3 black--text text-h6">{{ step.text }}</h6>
        </div>

        <GesuchCardAdressen
            v-if="step.ident === 'adressen'"
            :gesuch="gesuch"
            :loading="saving"
            :read-only="readOnly"
            @gesuchdatachanged="onGesuchDataChanged"
            @next="next"
        />
        <GesuchCardLiegenschaften
            v-else-if="step.ident === 'liegenschaften'"
            :gesuch="gesuch"
            :loading="saving"
            :read-only="readOnly"
            @editingmode="(e: boolean) => (liegenschaftEditingMode = e)"
            @gesuchdatachanged="onGesuchDataChanged"
            @next="next"
        />
        <GesuchCardProjekt
            v-else-if="step.ident === 'projekt'"
            :gesuch="gesuch"
            :loading="saving"
            :read-only="readOnly"
            @next="
                () => {
                    gesuch?.set('data', createModel(Data, gesuch?.get('data')))
                    next(null)
                }
            "
        />
        <GesuchCardDokumente
            v-else-if="step.ident === 'dokumente'"
            :gesuch="gesuch"
            :loading="saving"
            :read-only="readOnly"
            :zeus-dokumente="zeusDokumente"
            @gesuchdatachanged="onGesuchDataChanged"
            @next="next"
        />
        <GesuchCardBedingungen
            v-else-if="step.ident === 'bedingungen'"
            key="bedingungen"
            :gesuch="gesuch"
            type="foerderbedingungen"
            :loading="saving"
            :read-only="readOnly"
            @next="next"
        />
        <GesuchCardBedingungen
            v-else-if="step.ident === 'foerdersaetze'"
            key="foerdersaetze"
            type="foerdersaetze"
            :gesuch="gesuch"
            :loading="saving"
            :read-only="readOnly"
            @next="next"
        />
        <GesuchCardKontoinformation
            v-else-if="step.ident === 'kontoinformation'"
            :gesuch="gesuch"
            :loading="saving"
            :read-only="readOnly"
            @next="next"
        />
        <GesuchCardAbschluss
            v-else-if="step.ident === 'abschluss'"
            :gesuch="gesuch"
            :loading="saving"
            :read-only="readOnly"
            @gesuchdatachanged="onGesuchDataChanged"
            @next="abschluss"
        />
        <GesuchCardNachrichten
            v-else-if="step.ident === 'nachrichten'"
            :gesuch="gesuch"
            :loading="saving"
            :read-only="readOnly"
        />
    </div>
</template>

<style lang="scss" scoped>
.locked {
    color: red;
    font-size: 0.8em;
    text-shadow: 0 0.2pt 0 black;
}

.read-only {
    color: yellow;
    font-size: 0.8em;
    text-shadow: 0 0.2pt 0 black;
}
</style>
